import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, Download, Loader2, CheckCircle2, Trash2, ZoomIn, ZoomOut, Maximize2, MousePointerClick, AlertCircle, Pencil, Save, Undo2, Lock, MousePointer2, Square, Minus, MoveRight, Edit3 } from 'lucide-react';
import * as XLSX from 'xlsx';
import { api } from '../lib/api';
import { useOnb, putOnb } from '../lib/onboarding';
import { FP_VIEWBOX, FP_BLOCKS, FP_DECOR, FP_ZONES, blockTitle } from '../data/floorplanMap';

const SOURCES = [['all', 'Semua'], ['creator', 'Creators'], ['brand', 'Brand Booth'], ['fnb', 'F&B'], ['lineup', 'Line Up']];
const SRC_LABEL = { creator: 'Creator', brand: 'Brand Booth', fnb: 'F&B', lineup: 'Line Up', manual: 'Manual' };
const PRICE = { hall: 2000000, corridor: 2000000, merch: 6000000, sponsor: 15000000, fnb: 6000000, truck: 6000000 };
const fmtRp = (v) => `Rp${Math.round(Number(v) || 0).toLocaleString('id-ID')}`;
const withRetry = async (fn, n = 3) => { for (let i = 0; ; i += 1) { try { return await fn(); } catch (e) { const st = e && e.response ? e.response.status : 0; if (i >= n - 1 || (st && st < 500)) throw e; await new Promise((r) => setTimeout(r, 1200 * (i + 1))); } } };
const NEW_SIZE = { hall: [28, 28], corridor: [28, 28.6], merch: [43, 27.5], sponsor: [44, 42], fnb: [42, 41], truck: [58, 40] };
const EMPTY_LAYOUT = { blocks: {}, added: [], hidden: [], lines: [] };
const normLayout = (l) => ({ blocks: (l && l.blocks) || {}, added: (l && l.added) || [], hidden: (l && l.hidden) || [], lines: (l && l.lines) || [] });
const snap = (v) => Math.round(v * 2) / 2;
const newId = (p) => `${p}${Math.random().toString(36).slice(2, 9)}`;
const arrowHead = (x1, y1, x2, y2, w) => {
  const a = Math.atan2(y2 - y1, x2 - x1);
  const L = 7 + w * 2.2;
  const W = 4 + w * 1.4;
  const bx = x2 - L * Math.cos(a);
  const by = y2 - L * Math.sin(a);
  const px = -Math.sin(a) * W;
  const py = Math.cos(a) * W;
  return `${x2},${y2} ${bx + px},${by + py} ${bx - px},${by - py}`;
};
const CATS = [
  { k: 'hall', label: 'Cream', fill: '#F2C29B' },
  { k: 'merch', label: 'Hijau', fill: '#8CC63F' },
  { k: 'sponsor', label: 'Biru', fill: '#4FA3E0' },
  { k: 'fnb', label: 'Kuning', fill: '#F5E33B' },
];
const catOf = (z) => (z === 'corridor' ? 'hall' : z === 'truck' ? 'fnb' : z);
const isHex = (v) => /^#[0-9A-Fa-f]{6}$/.test(v || '');
const LINE_COLORS = ['#E53935', '#111111', '#43A047', '#2563EB', '#F5C518', '#A855F7'];

function BarterCell({ pk, value, onSave }) {
  const v = { type: 'barang', pct: 20, ...(value || {}) };
  const [pct, setPct] = useState(String(v.pct));
  useEffect(() => { setPct(String(v.pct)); }, [v.pct]);
  const commitPct = () => {
    let n = Number(String(pct).replace(',', '.'));
    if (!Number.isFinite(n)) n = 0;
    n = Math.min(100, Math.max(0, Math.round(n * 10) / 10));
    setPct(String(n));
    if (n !== v.pct) onSave(pk, { type: 'revenue', pct: n });
  };
  return (
    <div className="mt-1.5 space-y-1" onClick={(e) => e.stopPropagation()} data-testid={`fp-barter-${pk}`}>
      <div className="inline-flex rounded-md overflow-hidden border border-white/15">
        {[['barang', 'Barter'], ['revenue', 'Sharing revenue']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => { if (v.type !== k) onSave(pk, { type: k, pct: v.pct }); }}
            className={`px-2 py-0.5 text-[10px] font-bold ${v.type === k ? 'bg-[#A855F7] text-white' : 'text-white/50 hover:bg-white/10'}`}>{l}</button>
        ))}
      </div>
      {v.type === 'revenue' && (
        <div className="flex items-center gap-1 text-[11px] text-white/60">
          <input inputMode="decimal" value={pct} onChange={(e) => setPct(e.target.value.replace(/[^0-9.,]/g, ''))} onBlur={commitPct} onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            className="w-14 bg-black/30 border border-white/10 rounded px-1.5 py-0.5 text-white font-bold text-right focus:outline-none focus:border-white/40" data-testid={`fp-barter-pct-${pk}`} />
          <span>% Talesverse / {Math.round((100 - (Number(String(pct).replace(',', '.')) || 0)) * 10) / 10}% tenant</span>
        </div>
      )}
    </div>
  );
}

function PriceCell({ r, onSave }) {
  const [val, setVal] = useState(String(r.price));
  useEffect(() => { setVal(String(r.price)); }, [r.price]);
  const digits = String(val).replace(/[^0-9]/g, '');
  const commit = () => { if (!digits) { onSave(r.pk, null); return; } const n = Number(digits); if (n === r.price) return; onSave(r.pk, n === r.defPrice ? null : n); };
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-1"><span className="text-white/40 text-xs">Rp</span>
        <input inputMode="numeric" value={digits ? Number(digits).toLocaleString('id-ID') : ''} onChange={(e) => setVal(e.target.value.replace(/[^0-9]/g, ''))} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          className="w-28 bg-black/30 border border-white/10 rounded-md px-2 py-1 text-sm text-white font-bold focus:outline-none focus:border-white/40" data-testid={`fp-price-${r.b.id}`} /></div>
      <div className="text-[10px] text-white/40 mt-0.5">{r.custom ? <>custom | <button type="button" className="underline hover:text-white" onClick={() => onSave(r.pk, null)}>reset ke {fmtRp(r.defPrice)}</button></> : 'harga standar'}</div>
    </div>
  );
}
const SIZE = { hall: [2, 1.5], corridor: [2, 1.5], merch: [2, 3], sponsor: [3, 3], fnb: [3, 3], truck: [3, 3] };
const sizeTxt = (z) => `${String(SIZE[z][0]).replace('.', ',')}x${String(SIZE[z][1]).replace('.', ',')}m`;
const fmtNum = (v) => String(Math.round(v * 100) / 100).replace('.', ',');
const nameOf = (r) => String(r.brand_name || r.company_name || r.business_name || r.name || r.pic_name || '(tanpa nama)').trim();
const BLOCK_IDS = new Set(FP_BLOCKS.map((b) => b.id));
const DRAFT_KEY = 'tv_floorplan_draft';
const PAY = { lunas: { label: 'Lunas', dot: '#10B981', fg: '#fff' }, dp: { label: 'DP', dot: '#F5C518', fg: '#111' }, belum: { label: 'Belum bayar', dot: '#E63329', fg: '#fff' }, barter: { label: 'Barter', dot: '#A855F7', fg: '#fff' } };
const PAY_ORDER = ['lunas', 'dp', 'belum', 'barter'];
const UNSOLD_GROUPS = [
  { k: 'cream', label: 'Cream (Hall & Koridor)', zones: ['hall', 'corridor'], color: '#F2C29B' },
  { k: 'green', label: 'Hijau (Booth M)', zones: ['merch'], color: '#8CC63F' },
  { k: 'blue', label: 'Biru (Sponsor)', zones: ['sponsor'], color: '#4FA3E0' },
  { k: 'yellow', label: 'Kuning (F&B & Food Truck)', zones: ['fnb', 'truck'], color: '#F5E33B' },
];
const PAY_TINT = { lunas: '#86EFAC', dp: '#FDE68A', belum: '#FCA5A5', barter: '#D8B4FE' };
const payKey = (a) => (a.src === 'manual' ? `manual:${String(a.name || '').trim().toLowerCase().replace(/\./g, '_')}` : `${a.src}:${a.id}`);
const stable = (o) => JSON.stringify(Object.keys(o || {}).sort().map((k) => [k, o[k].src, o[k].id, o[k].name]));
const errText = (e, fb) => { const st = e && e.response ? e.response.status : ''; const d = e && e.response && e.response.data && e.response.data.detail; return `${fb}${st ? ` (kode ${st}${d ? `: ${typeof d === 'string' ? d : JSON.stringify(d).slice(0, 120)}` : ''})` : ' (koneksi terputus)'}`; };

function wrapName(name, w, h) {
  const maxLines = h >= 38 ? 4 : 3;
  for (let fs = 7; fs >= 3.6; fs -= 0.4) {
    const cpl = Math.max(3, Math.floor((w - 3) / (fs * 0.58)));
    const lines = [];
    let cur = '';
    name.split(/\s+/).filter(Boolean).forEach((word) => {
      let wd = word;
      while (wd.length > cpl) { if (cur) { lines.push(cur); cur = ''; } lines.push(wd.slice(0, cpl)); wd = wd.slice(cpl); }
      if (!wd) return;
      if (!cur) cur = wd; else if ((cur + ' ' + wd).length <= cpl) cur += ' ' + wd; else { lines.push(cur); cur = wd; }
    });
    if (cur) lines.push(cur);
    if (lines.length <= maxLines && (lines.length + 1) * fs * 1.12 <= h - 2) return { fs, lines };
  }
  const fs = 3.6;
  const cpl = Math.max(3, Math.floor((w - 3) / (fs * 0.58)));
  return { fs, lines: name.length > cpl * 2 ? [name.slice(0, cpl), name.slice(cpl, cpl * 2 - 1) + '...'] : [name.slice(0, cpl), name.slice(cpl)].filter(Boolean) };
}

function Decor() {
  return FP_DECOR.map((d, i) => {
    if (d[0] === 'r') return <rect key={i} x={d[1]} y={d[2]} width={d[3]} height={d[4]} fill={d[5]} stroke={d[6] || undefined} strokeWidth={d[7] || undefined} rx={d[8] || 0} strokeDasharray={d[9] || undefined} />;
    if (d[0] === 'p') return <path key={i} d={d[1]} fill={d[2]} stroke={d[3] || undefined} strokeWidth={d[4] || undefined} strokeDasharray={d[5] || undefined} />;
    return (
      <text key={i} x={d[1]} y={d[2]} fontSize={d[4]} fill={d[5]} fontWeight={d[6]} textAnchor="middle" dominantBaseline="middle"
        letterSpacing={d[8] || undefined} transform={d[7] ? `rotate(${d[7]} ${d[1]} ${d[2]})` : undefined} fontFamily="Arial, Helvetica, sans-serif">{d[3]}</text>
    );
  });
}

export default function FloorplanTab({ regs = [], corporate = [], fnb = [], lineup = [], onReload }) {
  const [assign, setAssign] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState('');
  const [selBlock, setSelBlock] = useState(null);
  const [pickTenant, setPickTenant] = useState(null);
  const [src, setSrc] = useState('all');
  const onb = useOnb();
  const [showUnfix, setShowUnfix] = useState(false);
  const [pksBusy, setPksBusy] = useState('');
  const [q, setQ] = useState('');
  const [manual, setManual] = useState('');
  const [showNames, setShowNames] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [hover, setHover] = useState(null);
  const [serverAssign, setServerAssign] = useState({});
  const [loadFailed, setLoadFailed] = useState(false);
  const [restored, setRestored] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [delPw, setDelPw] = useState('');
  const [delBusy, setDelBusy] = useState(false);
  const [delErr, setDelErr] = useState('');
  const [payments, setPayments] = useState({});
  const [payBusy, setPayBusy] = useState('');
  const [prices, setPrices] = useState({});
  const [barter, setBarter] = useState({});
  const [layout, setLayout] = useState(EMPTY_LAYOUT);
  const [serverLayout, setServerLayout] = useState(EMPTY_LAYOUT);
  const [editMode, setEditMode] = useState(false);
  const [tool, setTool] = useState('select');
  const [newZone, setNewZone] = useState('hall');
  const [newColor, setNewColor] = useState('');
  const scrollRef = useRef(null);
  const panRef = useRef(null);
  const panMovedRef = useRef(false);
  const [panning, setPanning] = useState(false);
  const autoRef = useRef(false);
  const [sel, setSel] = useState(null);
  const [drag, setDrag] = useState(null);
  const [layoutSaving, setLayoutSaving] = useState(false);
  const [layoutMsg, setLayoutMsg] = useState('');
  const BLOCKS_LIVE = useMemo(() => {
    const hid = new Set(layout.hidden || []);
    const ov = layout.blocks || {};
    return [...FP_BLOCKS, ...(layout.added || [])].filter((bk) => !hid.has(bk.id)).map((bk) => ({ ...bk, ...(ov[bk.id] || {}) }));
  }, [layout]);
  const layoutDirty = JSON.stringify(layout) !== JSON.stringify(serverLayout);
  const svgRef = useRef(null);

  const loadServer = () => {
    setLoaded(false);
    withRetry(() => api.get('/floorplan'))
      .then(({ data }) => {
        const srv = (data && data.assignments) || {};
        setServerAssign(srv);
        setPayments((data && data.payments) || {});
        setPrices((data && data.prices) || {});
        setBarter((data && data.barter) || {});
        { const lay = normLayout(data && data.layout); setLayout(lay); setServerLayout(lay); }
        setLoadFailed(false);
        setErr('');
        let draft = null;
        try { draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null'); } catch (e) { draft = null; }
        if (draft && typeof draft === 'object' && stable(draft) !== stable(srv)) { setAssign(draft); setRestored(true); if (!Object.keys(srv).length && Object.keys(draft).length) autoRef.current = true; }
        else { setAssign(srv); try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* abaikan */ } }
        if (data && data.updated_at) { const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(data.updated_at) ? data.updated_at : data.updated_at + 'Z'); if (!isNaN(d)) setSavedAt(d); }
      })
      .catch((e) => {
        setLoadFailed(true);
        setErr(errText(e, 'Gagal memuat data floorplan dari server. Tombol simpan dikunci supaya data di server tidak tertimpa'));
        try { const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null'); if (draft) setAssign(draft); } catch (er) { /* abaikan */ }
      })
      .finally(() => setLoaded(true));
  };
  useEffect(() => { loadServer(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = loaded && stable(assign) !== stable(serverAssign);
  const assignRef = useRef(assign);
  assignRef.current = assign;
  const changeCount = useMemo(() => {
    const keys = new Set([...Object.keys(assign), ...Object.keys(serverAssign)]);
    let n = 0;
    keys.forEach((k) => { const a = assign[k]; const b = serverAssign[k]; if (JSON.stringify(a ? [a.src, a.id, a.name] : null) !== JSON.stringify(b ? [b.src, b.id, b.name] : null)) n += 1; });
    return n;
  }, [assign, serverAssign]);

  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const saveNow = async () => {
    if (loadFailed || saving) return;
    setSaving(true);
    setErr('');
    try {
      const sent = assignRef.current;
      const { data } = await withRetry(() => api.put('/floorplan', { assignments: sent }));
      const srv = (data && data.assignments) || {};
      setServerAssign(srv);
      if (stable(assignRef.current) === stable(sent)) { setAssign(srv); try { localStorage.removeItem(DRAFT_KEY); } catch (er) { /* abaikan */ } }
      setRestored(false);
      setSavedAt(new Date());
      if (Object.keys(srv).length !== Object.keys(sent).length) setErr('Sebagian blok tidak tersimpan. Cek ulang placement-nya.');
    } catch (e) {
      setErr(errText(e, 'Gagal menyimpan placement. Perubahan masih aman di browser ini, coba klik Simpan lagi'));
    } finally {
      setSaving(false);
    }
  };
  useEffect(() => {
    if (!autoRef.current || !dirty || saving || loadFailed) return undefined;
    const t = setTimeout(() => { autoRef.current = false; saveNow(); }, 1200);
    return () => clearTimeout(t);
  }, [assign, dirty, saving, loadFailed]); // eslint-disable-line react-hooks/exhaustive-deps
  const discardDraft = () => {
    setAssign(serverAssign);
    setRestored(false);
    try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* abaikan */ }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { setPickTenant(null); setSelBlock(null); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const tenants = useMemo(() => {
    const mk = (arr, s) => (arr || []).filter((r) => (r.status || 'pending') === 'approved').map((r) => ({
      key: `${s}:${r.id}`, src: s, id: r.id, name: nameOf(r), partner: r.partner_brand_name || '',
      info: r.category || r.kategori || r.booth_package || r.pic_name || '',
    }));
    const base = [...mk(regs, 'creator'), ...mk(corporate, 'brand'), ...mk(fnb, 'fnb')];
    const names = new Set(base.map((t) => t.name.toLowerCase()));
    const creatorIds = new Set(base.filter((t) => t.src === 'creator').map((t) => t.id));
    const lu = (lineup || []).filter((e) => e && e.active !== false && String(e.name || '').trim() && !(e.source_reg_id && creatorIds.has(e.source_reg_id)) && !names.has(String(e.name).trim().toLowerCase()))
      .map((e) => ({ key: `lineup:${e.id}`, src: 'lineup', id: e.id, name: String(e.name).trim(), partner: '', info: e.role || '' }));
    const pksKeys = new Set((onb.items || []).filter((d) => d.pks && d.pks.signed && d.status !== 'cancelled').map((d) => `${d.src}:${d.ref_id}`));
    const man = (onb.items || []).filter((d) => d.src === 'manual' && d.status !== 'cancelled' && String(d.name || '').trim())
      .map((d) => ({ key: `manual:${d.id}`, src: 'manual', id: d.id, name: String(d.name).trim(), partner: '', info: d.category || '' }));
    return [...base, ...lu, ...man].map((t) => ({ ...t, pks: pksKeys.has(`${t.src}:${t.id}`) })).sort((a, b) => a.name.localeCompare(b.name, 'id'));
  }, [regs, corporate, fnb, lineup, onb.items]);
  const tenantByKey = useMemo(() => Object.fromEntries(tenants.map((t) => [t.key, t])), [tenants]);

  // Booth yang sudah dipilih di form Brand Booth (approved) -> otomatis tampil
  const auto = useMemo(() => {
    const out = {};
    (corporate || []).filter((r) => r.status === 'approved').forEach((r) => {
      const bp = String(r.booth_package || '').trim().toUpperCase();
      const id = /^M\d+$/.test(bp) ? bp : /^\d+$/.test(bp) ? `H${bp}` : null;
      if (id && BLOCK_IDS.has(id)) out[id] = { src: 'brand', id: r.id, name: nameOf(r), auto: true };
    });
    return out;
  }, [corporate]);

  const effective = useMemo(() => {
    const out = { ...auto };
    Object.entries(assign).forEach(([k, v]) => { if (v && v.src === 'none') delete out[k]; else if (v) out[k] = v; });
    Object.keys(out).forEach((k) => {
      const v = out[k];
      const t = tenantByKey[`${v.src}:${v.id}`];
      if (t) out[k] = { ...v, name: t.name, partner: t.partner };
      else if (v.src !== 'manual') out[k] = { ...v, missing: true };
    });
    return out;
  }, [assign, auto, tenantByKey]);

  const blocksOfTenant = useMemo(() => {
    const m = {};
    Object.entries(effective).forEach(([bid, v]) => { const k = `${v.src}:${v.id}`; (m[k] = m[k] || []).push(bid); });
    return m;
  }, [effective]);

  const persist = (next) => {
    setAssign(next);
    autoRef.current = true;
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(next)); } catch (e) { /* abaikan */ }
  };
  const assignTo = (bid, t) => persist({ ...assign, [bid]: { src: t.src, id: t.id, name: t.name } });
  const assignManual = (bid, name) => { const n = name.trim(); if (!n) return; persist({ ...assign, [bid]: { src: 'manual', id: '', name: n } }); setManual(''); };
  const clearBlock = (bid) => { const next = { ...assign }; if (auto[bid]) next[bid] = { src: 'none', id: '', name: '' }; else delete next[bid]; persist(next); };

  const doUnapprove = async () => {
    if (!delTarget || !delPw) return;
    setDelBusy(true);
    setDelErr('');
    try {
      await api.post('/floorplan/unapprove', { src: delTarget.src, id: delTarget.id, password: delPw });
      const strip = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => !(v && v.src === delTarget.src && v.id === delTarget.id)));
      setServerAssign((o) => strip(o));
      setAssign((o) => { const n = strip(o); try { if (localStorage.getItem(DRAFT_KEY)) localStorage.setItem(DRAFT_KEY, JSON.stringify(n)); } catch (e) { /* abaikan */ } return n; });
      if (pickTenant && pickTenant.key === delTarget.key) setPickTenant(null);
      setDelTarget(null);
      setDelPw('');
      if (onReload) onReload();
    } catch (e) {
      setDelErr(errText(e, 'Gagal membatalkan approval'));
    } finally {
      setDelBusy(false);
    }
  };

  const saveBarter = async (key, val) => {
    const prev = barter[key];
    setBarter((p) => ({ ...p, [key]: val }));
    try { await withRetry(() => api.put('/floorplan/barter', { key, type: val.type, pct: val.pct })); }
    catch (e) { setBarter((p) => { const n = { ...p }; if (prev) n[key] = prev; else delete n[key]; return n; }); setErr(errText(e, 'Gagal menyimpan detail barter')); }
  };
  const barterText = (pk) => { const bv = { type: 'barang', pct: 20, ...(barter[pk] || {}) }; return bv.type === 'revenue' ? `Sharing revenue ${bv.pct}% Talesverse / ${Math.round((100 - bv.pct) * 10) / 10}% tenant` : 'Barter'; };
  const setPrice = async (key, value) => {
    const prev = prices[key];
    const v = value == null ? null : Math.max(0, Math.round(Number(value)));
    if (v !== null && !Number.isFinite(v)) return;
    setPrices((p) => { const n = { ...p }; if (v === null) delete n[key]; else n[key] = v; return n; });
    try { await withRetry(() => api.put('/floorplan/price', { key, price: v })); }
    catch (e) { setPrices((p) => { const n = { ...p }; if (prev == null) delete n[key]; else n[key] = prev; return n; }); setErr(errText(e, 'Gagal menyimpan harga')); }
  };
  const setPay = async (key, status) => {
    const prev = payments[key] || '';
    if (prev === status) return;
    setPayments((p) => ({ ...p, [key]: status }));
    setPayBusy(key);
    try { await withRetry(() => api.put('/floorplan/payment', { key, status })); }
    catch (e) { setPayments((p) => ({ ...p, [key]: prev })); setErr(errText(e, 'Gagal menyimpan status pembayaran')); }
    finally { setPayBusy(''); }
  };
  const PayPills = ({ pk, size = 'sm' }) => (
    <div className="inline-flex rounded-lg overflow-hidden border border-white/15" onClick={(e) => e.stopPropagation()} data-testid={`fp-pay-${pk}`}>
      {PAY_ORDER.map((k) => {
        const on = (payments[pk] || 'belum') === k;
        return (
          <button key={k} type="button" disabled={payBusy === pk} onClick={() => setPay(pk, k)} title={PAY[k].label}
            className={`${size === 'sm' ? 'px-2 py-1 text-[11px]' : 'px-3 py-1.5 text-xs'} font-bold transition-colors disabled:opacity-60 ${on ? '' : 'text-white/50 hover:bg-white/10'}`}
            style={on ? { background: PAY[k].dot, color: PAY[k].fg } : undefined}>{k === 'belum' ? 'Belum' : PAY[k].label}</button>
        );
      })}
    </div>
  );

  // ---------- Edit Mode peta ----------
  const svgPt = (e) => {
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x: p.x, y: p.y };
  };
  const setOv = (id, patch) => setLayout((l) => ({ ...l, blocks: { ...l.blocks, [id]: { ...(l.blocks[id] || {}), ...patch } } }));
  const setLine = (id, patch) => setLayout((l) => ({ ...l, lines: l.lines.map((ln) => (ln.id === id ? { ...ln, ...patch } : ln)) }));
  const capture = (e) => { try { svgRef.current.setPointerCapture(e.pointerId); } catch (er) { /* abaikan */ } };
  const onSvgDown = (e) => {
    if (!editMode) return;
    const p = svgPt(e);
    if (tool === 'block') {
      const [w, h] = NEW_SIZE[newZone];
      const cnt = (layout.added || []).length + 1;
      const nb = { id: newId('X'), label: `N${cnt}`, zone: newZone, x: snap(p.x - w / 2), y: snap(p.y - h / 2), w, h };
      if (isHex(newColor)) nb.color = newColor;
      setLayout((l) => ({ ...l, added: [...l.added, nb] }));
      setSel({ kind: 'block', id: nb.id });
      setTool('select');
      return;
    }
    if (tool === 'line' || tool === 'arrow') {
      const ln = { id: newId('L'), x1: snap(p.x), y1: snap(p.y), x2: snap(p.x), y2: snap(p.y), arrow: tool === 'arrow' ? 'end' : 'none', color: tool === 'arrow' ? '#E53935' : '#111111', width: tool === 'arrow' ? 3 : 2, dash: false };
      setLayout((l) => ({ ...l, lines: [...l.lines, ln] }));
      setSel({ kind: 'line', id: ln.id });
      setDrag({ mode: 'draw', id: ln.id });
      capture(e);
      return;
    }
    setSel(null);
  };
  const startBlockMove = (e, bk) => {
    if (!editMode || tool !== 'select') return;
    e.stopPropagation();
    setSel({ kind: 'block', id: bk.id });
    setDrag({ mode: 'move', id: bk.id, start: svgPt(e), orig: { x: bk.x, y: bk.y } });
    capture(e);
  };
  const startResize = (e, bk) => {
    e.stopPropagation();
    setDrag({ mode: 'resize', id: bk.id, start: svgPt(e), orig: { w: bk.w, h: bk.h } });
    capture(e);
  };
  const startLineMove = (e, ln) => {
    if (!editMode || tool !== 'select') return;
    e.stopPropagation();
    setSel({ kind: 'line', id: ln.id });
    setDrag({ mode: 'moveLine', id: ln.id, start: svgPt(e), orig: { ...ln } });
    capture(e);
  };
  const startLineEnd = (e, ln, end) => {
    e.stopPropagation();
    setDrag({ mode: 'lineEnd', id: ln.id, end });
    capture(e);
  };
  const onSvgMove = (e) => {
    if (!drag) return;
    const p = svgPt(e);
    if (drag.mode === 'draw' || drag.mode === 'lineEnd') {
      const k = drag.mode === 'draw' || drag.end === 2 ? 2 : 1;
      setLine(drag.id, { [`x${k}`]: snap(p.x), [`y${k}`]: snap(p.y) });
      return;
    }
    const dx = p.x - drag.start.x;
    const dy = p.y - drag.start.y;
    if (drag.mode === 'move') setOv(drag.id, { x: snap(drag.orig.x + dx), y: snap(drag.orig.y + dy) });
    else if (drag.mode === 'resize') setOv(drag.id, { w: Math.max(8, snap(drag.orig.w + dx)), h: Math.max(8, snap(drag.orig.h + dy)) });
    else if (drag.mode === 'moveLine') setLine(drag.id, { x1: snap(drag.orig.x1 + dx), y1: snap(drag.orig.y1 + dy), x2: snap(drag.orig.x2 + dx), y2: snap(drag.orig.y2 + dy) });
  };
  const onSvgUp = () => {
    if (drag && drag.mode === 'draw') {
      const ln = layout.lines.find((x) => x.id === drag.id);
      if (ln && Math.hypot(ln.x2 - ln.x1, ln.y2 - ln.y1) < 4) { setLayout((l) => ({ ...l, lines: l.lines.filter((x) => x.id !== drag.id) })); setSel(null); }
      setTool('select');
    }
    setDrag(null);
  };
  const deleteSelected = () => {
    if (!sel) return;
    if (sel.kind === 'line') { setLayout((l) => ({ ...l, lines: l.lines.filter((x) => x.id !== sel.id) })); setSel(null); return; }
    if (effective[sel.id]) { setLayoutMsg('Blok ini masih terisi tenant. Kosongkan bloknya dulu sebelum dihapus dari peta.'); return; }
    setLayout((l) => {
      const isAdded = (l.added || []).some((x) => x.id === sel.id);
      const blocksOv = { ...l.blocks };
      delete blocksOv[sel.id];
      return isAdded ? { ...l, added: l.added.filter((x) => x.id !== sel.id), blocks: blocksOv } : { ...l, hidden: [...l.hidden, sel.id] };
    });
    setSel(null);
  };
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const saveLayout = async () => {
    if (layoutSaving) return;
    setLayoutSaving(true); setLayoutMsg('');
    try {
      const sent = layoutRef.current;
      const { data } = await withRetry(() => api.put('/floorplan/layout', { layout: sent }));
      const lay = normLayout(data && data.layout);
      setServerLayout(lay);
      if (JSON.stringify(layoutRef.current) === JSON.stringify(sent)) setLayout(lay);
      setLayoutMsg('Layout peta tersimpan ke database.');
    } catch (e) { setLayoutMsg(errText(e, 'Gagal menyimpan layout peta')); }
    finally { setLayoutSaving(false); }
  };
  useEffect(() => {
    if (!layoutDirty || drag || layoutSaving) return undefined;
    const t = setTimeout(() => { saveLayout(); }, 1500);
    return () => clearTimeout(t);
  }, [layout, drag]); // eslint-disable-line react-hooks/exhaustive-deps
  const selBlockLive = sel && sel.kind === 'block' ? BLOCKS_LIVE.find((x) => x.id === sel.id) : null;
  const selLine = sel && sel.kind === 'line' ? (layout.lines || []).find((x) => x.id === sel.id) : null;
  useEffect(() => {
    if (!editMode) return undefined;
    const h = (e) => { if ((e.key === 'Delete' || e.key === 'Backspace') && sel && !/input|textarea|select/i.test((e.target && e.target.tagName) || '')) { e.preventDefault(); deleteSelected(); } };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });
  useEffect(() => {
    if (!layoutDirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [layoutDirty]);

  const onPanDown = (e) => {
    if (e.button !== 0) return;
    if (!editMode && e.pointerType === 'touch') return;
    if (editMode && tool !== 'select') return;
    const el = scrollRef.current;
    if (!el || (el.scrollWidth <= el.clientWidth + 1 && el.scrollHeight <= el.clientHeight + 1)) return;
    panRef.current = { x: e.clientX, y: e.clientY, sl: el.scrollLeft, st: el.scrollTop };
    panMovedRef.current = false;
    const move = (ev) => {
      const p = panRef.current;
      if (!p) return;
      const dx = ev.clientX - p.x;
      const dy = ev.clientY - p.y;
      if (!panMovedRef.current && Math.hypot(dx, dy) < 5) return;
      if (!panMovedRef.current) { panMovedRef.current = true; setPanning(true); }
      el.scrollLeft = p.sl - dx;
      el.scrollTop = p.st - dy;
    };
    const up = () => {
      panRef.current = null;
      setPanning(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      setTimeout(() => { panMovedRef.current = false; }, 0);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  const onBlockClick = (b) => {
    if (panMovedRef.current) return;
    if (editMode) return;
    if (pickTenant) { assignTo(b.id, pickTenant); setPickTenant(null); setSelBlock(b.id); return; }
    setSelBlock(b.id === selBlock ? null : b.id);
    setQ(''); setManual('');
  };

  const ql = q.trim().toLowerCase();
  const matchQ = (t) => !ql || t.name.toLowerCase().includes(ql) || (t.partner || '').toLowerCase().includes(ql);
  const listTenants = tenants.filter((t) => (src === 'all' || t.src === src) && (showUnfix || t.pks) && matchQ(t));
  const unfixCount = tenants.filter((t) => !t.pks && (src === 'all' || t.src === src)).length;
  const unplaced = tenants.filter((t) => t.pks && !blocksOfTenant[t.key]);
  const isUnfix = (a) => !!a && !a.missing && !(tenantByKey[`${a.src}:${a.id}`] || {}).pks;
  const markPks = async (t) => {
    setPksBusy(t.key);
    try { const { data } = await api.post('/onboarding/pks', { src: t.src, ref_id: t.id, name: t.name, signed: true }); putOnb(data); }
    catch (e) { setErr(errText(e, 'Gagal menandai PKS')); }
    finally { setPksBusy(''); }
  };
  const filled = Object.keys(effective).length;
  const hiBlocks = useMemo(() => {
    if (!ql) return null;
    return new Set(Object.entries(effective).filter(([, v]) => (v.name || '').toLowerCase().includes(ql)).map(([k]) => k));
  }, [ql, effective]);

  const ZONE_ORDER = ['corridor', 'hall', 'merch', 'sponsor', 'fnb', 'truck'];
  const numOf = (lab) => parseInt(String(lab).replace(/\D/g, ''), 10) || 0;
  // Nama tenant sama -> digabung jadi 1 baris, space booth dijumlah
  const placedRows = useMemo(() => {
    const rows = BLOCKS_LIVE.filter((b) => effective[b.id]).map((b) => ({ b, a: effective[b.id] }))
      .sort((x, y) => (ZONE_ORDER.indexOf(x.b.zone) - ZONE_ORDER.indexOf(y.b.zone)) || (numOf(x.b.label) - numOf(y.b.label)));
    const groups = [];
    const idx = {};
    rows.forEach(({ b, a }) => {
      const k = (a.name || '').trim().toLowerCase() || `${a.src}:${a.id}`;
      if (idx[k] == null) { idx[k] = groups.length; groups.push({ key: k, a, blocks: [] }); }
      else if (groups[idx[k]].a.src === 'manual' && a.src !== 'manual') groups[idx[k]].a = a;
      groups[idx[k]].blocks.push(b);
    });
    return groups.map((g) => {
      const bySize = {};
      let area = 0;
      g.blocks.forEach((b) => { const t = sizeTxt(b.zone); bySize[t] = (bySize[t] || 0) + 1; area += SIZE[b.zone][0] * SIZE[b.zone][1]; });
      const sizeLabel = Object.entries(bySize).map(([t, n]) => (n > 1 ? `${n} x (${t})` : t)).join(' + ');
      const zones = [...new Set(g.blocks.map((b) => FP_ZONES[b.zone].name))].join(', ');
      const pk = payKey(g.a);
      const defPrice = g.blocks.reduce((n, bb) => n + (PRICE[bb.zone] || 0), 0);
      return { ...g, pk, defPrice, price: prices[pk] != null ? prices[pk] : defPrice, custom: prices[pk] != null, b: g.blocks[0], area, sizeLabel, zones, blockText: g.blocks.map((b) => blockTitle(b)).join(', ') };
    });
  }, [effective, prices, BLOCKS_LIVE]);
  const totalBlocks = placedRows.reduce((n, r) => n + r.blocks.length, 0);
  const totalArea = placedRows.reduce((n, r) => n + r.area, 0);
  const payTotal = placedRows.reduce((m, r) => { const k = payments[r.pk] || 'belum'; m[k] = (m[k] || 0) + r.price; return m; }, {});
  const grandTotal = placedRows.reduce((n, r) => n + r.price, 0);
  const revCount = placedRows.filter((r) => payments[r.pk] === 'barter' && (barter[r.pk] || {}).type === 'revenue').length;
  const payCount = placedRows.reduce((m, r) => { const k = payments[r.pk] || 'belum'; m[k] = (m[k] || 0) + 1; return m; }, {});
  const unsold = useMemo(() => {
    const groups = UNSOLD_GROUPS.map((g) => {
      const all = BLOCKS_LIVE.filter((b) => g.zones.includes(b.zone));
      const empty = all.filter((b) => !effective[b.id]);
      return { ...g, total: all.length, empty, value: empty.reduce((n, b) => n + (PRICE[b.zone] || 0), 0), area: empty.reduce((n, b) => n + SIZE[b.zone][0] * SIZE[b.zone][1], 0) };
    });
    const count = groups.reduce((n, g) => n + g.empty.length, 0);
    const total = groups.reduce((n, g) => n + g.total, 0);
    return { groups, count, total, value: groups.reduce((n, g) => n + g.value, 0), area: groups.reduce((n, g) => n + g.area, 0) };
  }, [BLOCKS_LIVE, effective]);
  const shownRows = ql ? placedRows.filter((r) => (r.a.name || '').toLowerCase().includes(ql) || (r.a.partner || '').toLowerCase().includes(ql) || r.blockText.toLowerCase().includes(ql)) : placedRows;
  const exportXlsx = () => {
    const rows = placedRows.map((r, i) => {
      const st = payments[r.pk] || 'belum';
      const bv = { type: 'barang', pct: 20, ...(barter[r.pk] || {}) };
      return {
        No: i + 1, Tenant: r.a.name, Blok: r.blockText, 'Jumlah Blok': r.blocks.length, Zona: r.zones, 'Ukuran Booth': r.sizeLabel,
        'Luas (m2)': r.area, 'Harga (Rp)': r.price, 'Harga Standar (Rp)': r.defPrice, 'Harga Custom': r.custom ? 'Ya' : '',
        'Status Pembayaran': PAY[st].label, 'Detail Barter': st === 'barter' ? (bv.type === 'revenue' ? 'Sharing revenue' : 'Barter') : '',
        'Revenue Talesverse (%)': st === 'barter' && bv.type === 'revenue' ? bv.pct : '', 'Revenue Tenant (%)': st === 'barter' && bv.type === 'revenue' ? Math.round((100 - bv.pct) * 10) / 10 : '',
        'Sharing dengan': r.a.partner || '', Sumber: (SRC_LABEL[r.a.src] || r.a.src) + (r.a.auto ? ' (otomatis)' : ''),
      };
    });
    const summary = PAY_ORDER.map((k) => ({ Status: PAY[k].label, 'Jumlah Tenant': payCount[k] || 0, 'Total Harga (Rp)': payTotal[k] || 0 }))
      .concat([{ Status: 'TOTAL', 'Jumlah Tenant': placedRows.length, 'Total Harga (Rp)': grandTotal }]);
    const perBlock = BLOCKS_LIVE.map((bk) => {
      const a = effective[bk.id];
      return { Blok: blockTitle(bk), Zona: FP_ZONES[bk.zone].name, Ukuran: sizeTxt(bk.zone), 'Harga Standar (Rp)': PRICE[bk.zone] || 0, Tenant: a ? a.name : '', Status: a ? PAY[payments[payKey(a)] || 'belum'].label : 'Kosong' };
    });
    const wb = XLSX.utils.book_new();
    const add = (data, name, widths) => { const ws = XLSX.utils.json_to_sheet(data); ws['!cols'] = widths.map((w) => ({ wch: w })); XLSX.utils.book_append_sheet(wb, ws, name); };
    add(rows, 'Placement', [5, 30, 26, 10, 18, 20, 10, 14, 16, 12, 16, 16, 20, 16, 22, 22]);
    add(summary, 'Ringkasan', [18, 14, 18]);
    add(perBlock, 'Per Blok', [12, 14, 10, 18, 30, 14]);
    const unsoldSum = unsold.groups.map((g) => ({ Kategori: g.label, 'Total Blok': g.total, 'Sudah Terisi': g.total - g.empty.length, 'Belum Laku': g.empty.length, 'Luas Belum Laku (m2)': g.area, 'Potensi (Rp)': g.value, 'Blok Kosong': g.empty.map((b) => blockTitle(b)).join(', ') }))
      .concat([{ Kategori: 'TOTAL', 'Total Blok': unsold.total, 'Sudah Terisi': unsold.total - unsold.count, 'Belum Laku': unsold.count, 'Luas Belum Laku (m2)': unsold.area, 'Potensi (Rp)': unsold.value, 'Blok Kosong': '' }]);
    add(unsoldSum, 'Belum Laku', [28, 11, 13, 11, 20, 16, 80]);
    XLSX.writeFile(wb, `floorplan-talesverse-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };
  const exportCsv = () => {
    const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const lines = [['Tenant', 'Blok', 'Jumlah Blok', 'Zona', 'Ukuran Booth', 'Total Luas (m2)', 'Sharing dengan', 'Sumber', 'Pembayaran', 'Detail Barter', 'Harga (Rp)'].map(esc).join(',')]
      .concat(placedRows.map((r) => [r.a.name, r.blockText, r.blocks.length, r.zones, r.sizeLabel, fmtNum(r.area), r.a.partner || '', (SRC_LABEL[r.a.src] || r.a.src) + (r.a.auto ? ' (otomatis)' : ''), PAY[payments[r.pk] || 'belum'].label, payments[r.pk] === 'barter' ? barterText(r.pk) : '', r.price].map(esc).join(',')));
    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const el = document.createElement('a'); el.href = URL.createObjectURL(blob); el.download = 'floorplan-tenant-talesverse.csv';
    document.body.appendChild(el); el.click(); el.remove(); setTimeout(() => URL.revokeObjectURL(el.href), 2000);
  };

  const selB = selBlock ? BLOCKS_LIVE.find((b) => b.id === selBlock) : null;
  const selA = selB ? effective[selB.id] : null;

  const exportImage = (type) => {
    const svg = svgRef.current;
    if (!svg) return;
    const clone = svg.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('width', FP_VIEWBOX.w * 2);
    clone.setAttribute('height', FP_VIEWBOX.h * 2);
    clone.removeAttribute('style');
    clone.querySelectorAll('[data-ui]').forEach((n) => n.remove());
    const str = new XMLSerializer().serializeToString(clone);
    const dl = (blob, name) => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 2000); };
    if (type === 'svg') { dl(new Blob([str], { type: 'image/svg+xml' }), 'floorplan-talesverse.svg'); return; }
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = FP_VIEWBOX.w * 2; c.height = FP_VIEWBOX.h * 2;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(img, 0, 0, c.width, c.height);
      c.toBlob((b) => b && dl(b, 'floorplan-talesverse.png'), 'image/png');
    };
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(str);
  };

  const fmtTime = (d) => d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="space-y-4" data-testid="floorplan-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-white">Floorplan Mapping</h2>
          <p className="text-sm text-white/50">Klik blok di peta lalu pilih tenant yang sudah fix (sudah PKS). The Brickhall Fatmawati | 12-13 Des 2026</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-white/50 inline-flex items-center gap-1.5 mr-1" data-testid="floorplan-save-state">
            {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Menyimpan...</>
              : dirty ? <span className="text-[#F5C518] font-bold">Belum disimpan ({changeCount} perubahan)</span>
                : savedAt ? <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Tersimpan {fmtTime(savedAt)}</> : null}
          </span>
          {dirty && (
            <button type="button" onClick={discardDraft} disabled={saving} className="px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-white/80 font-bold inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="floorplan-discard"><Undo2 className="w-3.5 h-3.5" /> Batal</button>
          )}
          <button type="button" onClick={saveNow} disabled={!dirty || saving || loadFailed} className="px-4 py-2 rounded-lg font-black text-white inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg" style={{ background: 'var(--tv-accent, #E63329)' }} data-testid="floorplan-save"><Save className="w-4 h-4" /> Simpan Placement</button>
          <button type="button" onClick={() => setShowNames((v) => !v)} className="px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-white/80 font-bold" data-testid="floorplan-toggle-names">
            {showNames ? 'Tampilkan nomor' : 'Tampilkan nama'}
          </button>
          <button type="button" onClick={() => exportImage('png')} className="px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-white/80 font-bold inline-flex items-center gap-1.5" data-testid="floorplan-export-png"><Download className="w-3.5 h-3.5" /> PNG</button>
          <button type="button" onClick={() => exportImage('svg')} className="px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-white/80 font-bold inline-flex items-center gap-1.5" data-testid="floorplan-export-svg"><Download className="w-3.5 h-3.5" /> SVG</button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[['Total blok', BLOCKS_LIVE.length, 'text-white'], ['Terisi', filled, 'text-emerald-400'], ['Kosong', BLOCKS_LIVE.length - filled, 'text-[#F5C518]'], ['Tenant fix belum dapat blok', unplaced.length, 'text-[#E63329]']].map(([l, v, c]) => (
          <div key={l} className="tv-card p-3"><div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{l}</div><div className={`text-2xl font-black ${c}`}>{v}</div></div>
        ))}
      </div>

      {restored && dirty && (
        <div className="tv-card p-3 text-sm text-white flex flex-wrap items-center justify-between gap-3 border-l-4 border-l-[#F5C518]" data-testid="floorplan-restored">
          <span>Ada placement yang <b>belum disimpan</b> dari sesi sebelumnya dan sudah dipulihkan. Klik <b>Simpan Placement</b> untuk menyimpan ke server.</span>
          <button type="button" onClick={discardDraft} className="text-xs font-bold px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10">Buang draft</button>
        </div>
      )}
      {err && <div className="tv-card p-3 text-sm text-red-300 flex items-center gap-2"><AlertCircle className="w-4 h-4 flex-shrink-0" /><span className="flex-1">{err}</span>{loadFailed && <button type="button" onClick={loadServer} className="text-xs font-bold px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-white">Muat ulang</button>}</div>}
      {dirty && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[70] tv-card px-4 py-3 shadow-2xl flex items-center gap-3 border border-[#F5C518]/40" data-testid="floorplan-savebar">
          <span className="text-sm text-white"><b className="text-[#F5C518]">{changeCount} perubahan</b> belum disimpan</span>
          <button type="button" onClick={discardDraft} disabled={saving} className="text-xs font-bold px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-white/80 disabled:opacity-40">Batal</button>
          <button type="button" onClick={saveNow} disabled={saving || loadFailed} className="text-sm font-black px-4 py-2 rounded-lg text-white inline-flex items-center gap-1.5 disabled:opacity-40" style={{ background: 'var(--tv-accent, #E63329)' }}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Simpan Placement</button>
        </div>
      )}
      {pickTenant && (
        <div className="tv-card p-3 text-sm text-white flex items-center justify-between gap-3 border-l-4" style={{ borderLeftColor: 'var(--tv-accent)' }} data-testid="floorplan-pick-hint">
          <span className="inline-flex items-center gap-2"><MousePointerClick className="w-4 h-4" /> Klik blok di peta untuk menaruh <b>{pickTenant.name}</b></span>
          <button type="button" onClick={() => setPickTenant(null)} className="p-1 rounded hover:bg-white/10"><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-4 items-start">
        <div className="tv-card p-2">
          <div className="flex items-center justify-between px-1 pb-2 gap-2 flex-wrap">
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-white/60">
              {[['hall', `Hall / Koridor (${sizeTxt('hall')})`], ['merch', `Booth M (${sizeTxt('merch')})`], ['sponsor', `Sponsor (${sizeTxt('sponsor')})`], ['fnb', `F&B / Food Truck (${sizeTxt('fnb')})`]].map(([z, l]) => (
                <span key={z} className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border" style={{ background: FP_ZONES[z].fill, borderColor: FP_ZONES[z].stroke }} />{l}</span>
              ))}
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border-2 border-[#111] bg-white" />Terisi = garis tebal, warna blok = status bayar:</span>
              {PAY_ORDER.map((k) => <span key={k} className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full border border-white" style={{ background: PAY[k].dot }} />{PAY[k].label}</span>)}
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border-2 border-dashed" style={{ borderColor: '#F97316' }} />Belum PKS</span>
            </div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white" title="Perkecil"><ZoomOut className="w-4 h-4" /></button>
              <span className="text-[11px] text-white/50 w-10 text-center">{Math.round(zoom * 100)}%</span>
              <button type="button" onClick={() => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white" title="Perbesar"><ZoomIn className="w-4 h-4" /></button>
              <button type="button" onClick={() => setZoom(1)} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white" title="Pas layar"><Maximize2 className="w-4 h-4" /></button>
              <button type="button" onClick={() => { setEditMode((v) => !v); setSel(null); setTool('select'); setLayoutMsg(''); setSelBlock(null); setPickTenant(null); }}
                className={`ml-1 px-3 py-1.5 rounded-lg text-xs font-black inline-flex items-center gap-1.5 ${editMode ? 'bg-[#F5C518] text-black' : 'bg-white/5 hover:bg-white/10 text-white'}`} data-testid="floorplan-edit-toggle"><Edit3 className="w-3.5 h-3.5" /> {editMode ? 'Selesai Edit' : 'Edit Map'}</button>
            </div>
          </div>
          {(editMode || layoutDirty) && (
            <div className="mb-2 rounded-xl border border-[#F5C518]/40 bg-[#F5C518]/10 p-2 flex flex-wrap items-center gap-2 text-xs text-white" data-testid="floorplan-edit-bar">
              {editMode && (
                <>
                  <div className="inline-flex rounded-lg overflow-hidden border border-white/15">
                    {[['select', 'Pilih / Geser', MousePointer2], ['block', '+ Blok', Square], ['line', 'Garis', Minus], ['arrow', 'Panah', MoveRight]].map(([k, l, Ic]) => (
                      <button key={k} type="button" onClick={() => setTool(k)} data-testid={`fp-tool-${k}`}
                        className={`px-2.5 py-1.5 font-bold inline-flex items-center gap-1 ${tool === k ? 'bg-[#F5C518] text-black' : 'text-white/80 hover:bg-white/10'}`}><Ic className="w-3.5 h-3.5" />{l}</button>
                    ))}
                  </div>
                  {tool === 'block' && (
                    <span className="inline-flex items-center gap-1.5 flex-wrap">
                      <span className="text-white/60">Kategori</span>
                      <select value={newZone} onChange={(e) => { setNewZone(e.target.value); setNewColor(''); }} className="bg-[#1c1414] border border-white/15 rounded-lg px-2 py-1.5 text-white" style={{ colorScheme: 'dark' }} data-testid="fp-new-zone">
                        {CATS.map((c) => <option key={c.k} value={c.k}>{c.label} - {FP_ZONES[c.k].name} ({fmtRp(PRICE[c.k])}, {sizeTxt(c.k)})</option>)}
                      </select>
                      <span className="text-white/60 ml-1">Warna</span>
                      <input type="color" value={isHex(newColor) ? newColor : FP_ZONES[newZone].fill} onChange={(e) => setNewColor(e.target.value)} className="w-8 h-7 rounded cursor-pointer bg-transparent border border-white/15" data-testid="fp-new-color" />
                      <input value={newColor} placeholder={FP_ZONES[newZone].fill} maxLength={7} onChange={(e) => { const v = e.target.value.trim(); setNewColor(v && v[0] !== '#' ? `#${v}` : v); }} className={`w-20 bg-[#1c1414] border rounded-md px-2 py-1 text-white font-mono ${newColor && !isHex(newColor) ? 'border-red-400' : 'border-white/15'}`} data-testid="fp-new-hex" />
                      {newColor && <button type="button" onClick={() => setNewColor('')} className="text-white/60 underline">default</button>}
                    </span>
                  )}
                  {selBlockLive && (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="text-white/60">Label</span>
                      <input value={selBlockLive.label} maxLength={10} onChange={(e) => setOv(selBlockLive.id, { label: e.target.value })} className="w-20 bg-black/40 border border-white/15 rounded-md px-2 py-1 text-white font-bold" data-testid="fp-sel-label" />
                      <span className="text-white/50">{Math.round(selBlockLive.w)} x {Math.round(selBlockLive.h)}</span>
                      <select value={catOf(selBlockLive.zone)} onChange={(e) => setOv(selBlockLive.id, { zone: e.target.value })} className="bg-[#1c1414] border border-white/15 rounded-lg px-2 py-1 text-white" style={{ colorScheme: 'dark' }} title="Kategori / harga" data-testid="fp-sel-cat">
                        {CATS.map((c) => <option key={c.k} value={c.k}>{c.label} ({fmtRp(PRICE[c.k])})</option>)}
                      </select>
                      <input type="color" value={selBlockLive.color || FP_ZONES[selBlockLive.zone].fill} onChange={(e) => setOv(selBlockLive.id, { color: e.target.value })} className="w-8 h-7 rounded cursor-pointer bg-transparent border border-white/15" title="Warna blok" data-testid="fp-sel-color" />
                      <input key={`hex-${selBlockLive.id}-${selBlockLive.color || ''}`} defaultValue={selBlockLive.color || ''} placeholder={FP_ZONES[selBlockLive.zone].fill} maxLength={7} onBlur={(e) => { let v = e.target.value.trim(); if (v && v[0] !== '#') v = `#${v}`; if (!v) setOv(selBlockLive.id, { color: '' }); else if (isHex(v)) setOv(selBlockLive.id, { color: v }); }} onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} className="w-20 bg-[#1c1414] border border-white/15 rounded-md px-2 py-1 text-white font-mono" title="Kode hex, Enter untuk terapkan" data-testid="fp-sel-hex" />
                      {selBlockLive.color && <button type="button" onClick={() => setOv(selBlockLive.id, { color: '' })} className="text-white/60 underline">warna default</button>}
                    </span>
                  )}
                  {selLine && (
                    <span className="inline-flex items-center gap-1.5">
                      {LINE_COLORS.map((c) => <button key={c} type="button" onClick={() => setLine(selLine.id, { color: c })} className={`w-5 h-5 rounded-full border-2 ${selLine.color === c ? 'border-white' : 'border-transparent'}`} style={{ background: c }} />)}
                      <select value={selLine.arrow} onChange={(e) => setLine(selLine.id, { arrow: e.target.value })} className="bg-[#1c1414] border border-white/15 rounded-lg px-2 py-1 text-white">
                        <option value="none">Tanpa panah</option><option value="end">Panah ujung</option><option value="both">Panah 2 arah</option>
                      </select>
                      <select value={selLine.width} onChange={(e) => setLine(selLine.id, { width: Number(e.target.value) })} className="bg-[#1c1414] border border-white/15 rounded-lg px-2 py-1 text-white">
                        {[1, 2, 3, 4, 6].map((w) => <option key={w} value={w}>Tebal {w}</option>)}
                      </select>
                      <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!selLine.dash} onChange={(e) => setLine(selLine.id, { dash: e.target.checked })} /> Putus-putus</label>
                    </span>
                  )}
                  {sel && <button type="button" onClick={deleteSelected} className="px-2.5 py-1.5 rounded-lg border border-white/15 hover:bg-red-500/20 font-bold inline-flex items-center gap-1" data-testid="fp-delete-sel"><Trash2 className="w-3.5 h-3.5" /> Hapus</button>}
                </>
              )}
              <span className="flex-1 text-white/60">{layoutMsg || (editMode ? (tool === 'block' ? 'Klik di peta untuk menaruh blok baru.' : tool === 'line' || tool === 'arrow' ? 'Tarik (klik-geser) di peta untuk menggambar.' : 'Geser blok/garis untuk memindah, tarik kotak kecil di pojok untuk resize. Tombol Delete untuk hapus.') : 'Ada perubahan layout peta yang belum disimpan.')}</span>
              {layoutDirty && <button type="button" onClick={() => { setLayout(serverLayout); setSel(null); setLayoutMsg(''); }} className="px-2.5 py-1.5 rounded-lg border border-white/15 hover:bg-white/10 font-bold">Batal</button>}
              <button type="button" onClick={saveLayout} disabled={!layoutDirty || layoutSaving} className="px-3 py-1.5 rounded-lg font-black text-white inline-flex items-center gap-1.5 disabled:opacity-40" style={{ background: 'var(--tv-accent, #E63329)' }} data-testid="fp-save-layout">{layoutSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Simpan Layout</button>
            </div>
          )}
          <div className="relative">
          <div ref={scrollRef} onPointerDown={onPanDown} className="overflow-auto rounded-xl bg-white select-none" style={{ maxHeight: '78vh', cursor: panning ? 'grabbing' : (zoom > 1 && (!editMode || tool === 'select') ? 'grab' : undefined) }} data-testid="floorplan-scroll">
            {!loaded ? (
              <div className="h-80 grid place-items-center text-black/50"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : (
              <svg ref={svgRef} viewBox={`0 0 ${FP_VIEWBOX.w} ${FP_VIEWBOX.h}`} style={{ width: `${zoom * 100}%`, minWidth: 880, display: 'block', touchAction: editMode ? 'none' : 'auto', cursor: editMode && tool !== 'select' ? 'crosshair' : 'inherit' }} data-testid="floorplan-svg"
                onPointerDown={editMode ? onSvgDown : undefined} onPointerMove={editMode ? onSvgMove : undefined} onPointerUp={editMode ? onSvgUp : undefined}>
                <rect x="0" y="0" width={FP_VIEWBOX.w} height={FP_VIEWBOX.h} fill="#ffffff" />
                <Decor />
                {BLOCKS_LIVE.map((b) => {
                  const z = FP_ZONES[b.zone];
                  const a = effective[b.id];
                  const isSel = selBlock === b.id;
                  const isHi = hiBlocks && hiBlocks.has(b.id);
                  const dim = hiBlocks && !isHi;
                  const label = a && showNames ? wrapName(a.name, b.w, b.h) : null;
                  const numFs = b.zone === 'fnb' || b.zone === 'truck' ? 10 : 9;
                  return (
                    <g key={b.id} onClick={() => onBlockClick(b)} onPointerDown={editMode ? (e) => startBlockMove(e, b) : undefined} onMouseEnter={() => setHover(b.id)} onMouseLeave={() => setHover((h) => (h === b.id ? null : h))}
                      style={{ cursor: panning ? 'grabbing' : editMode ? (tool === 'select' ? 'move' : 'crosshair') : 'pointer' }} opacity={dim ? 0.35 : 1} data-testid={`fp-block-${b.id}`}>
                      <title>{`${blockTitle(b)}${a ? ' - ' + a.name : ' - kosong'}`}</title>
                      <rect x={b.x} y={b.y} width={b.w} height={b.h} fill={b.color || z.fill} stroke={a ? '#111' : z.stroke} strokeWidth={a ? 1.6 : 0.8} />
                      {a && <rect x={b.x + 1.5} y={b.y + 1.5} width={b.w - 3} height={b.h - 3} fill={a.missing ? '#ffffff' : PAY_TINT[payments[payKey(a)] || 'belum']} opacity={a.missing ? 0.35 : 1} />}
                      {a && <circle cx={b.x + b.w - 3.4} cy={b.y + 3.4} r={2.4} fill={PAY[payments[payKey(a)] || 'belum'].dot} stroke="#ffffff" strokeWidth={0.7} />}
                      {label ? (
                        <>
                          <text x={b.x + 2} y={b.y + 2 + label.fs * 0.9} fontSize={label.fs * 0.85} fontWeight="800" fill="#7a3d12" fontFamily="Arial, Helvetica, sans-serif">{b.label}</text>
                          <text x={b.x + b.w / 2} fontSize={label.fs} fontWeight="700" fill={a.missing ? '#b91c1c' : '#111'} textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif">
                            {label.lines.map((ln, i) => (
                              <tspan key={i} x={b.x + b.w / 2} y={b.y + b.h / 2 + label.fs * 0.6 + (i - (label.lines.length - 1) / 2) * label.fs * 1.12 + 1}>{ln}</tspan>
                            ))}
                          </text>
                        </>
                      ) : (
                        <text x={b.x + b.w / 2} y={b.y + b.h / 2} fontSize={numFs} fontWeight="700" fill="#111" textAnchor="middle" dominantBaseline="middle" fontFamily="Arial, Helvetica, sans-serif">{b.label}</text>
                      )}
                      {isUnfix(a) && !isSel && (
                        <rect data-ui="1" x={b.x + 0.8} y={b.y + 0.8} width={b.w - 1.6} height={b.h - 1.6} fill="none" stroke="#F97316" strokeWidth={1.8} strokeDasharray="3 2" pointerEvents="none" />
                      )}
                      {(isSel || isHi || hover === b.id) && (
                        <rect data-ui="1" x={b.x - 1.5} y={b.y - 1.5} width={b.w + 3} height={b.h + 3} fill="none" stroke={isSel ? '#E63329' : isHi ? '#7c3aed' : '#2563eb'} strokeWidth={isSel ? 3 : 2} pointerEvents="none" />
                      )}
                      {editMode && sel && sel.kind === 'block' && sel.id === b.id && (
                        <>
                          <rect data-ui="1" x={b.x - 2} y={b.y - 2} width={b.w + 4} height={b.h + 4} fill="none" stroke="#2563eb" strokeWidth={1.5} strokeDasharray="4 3" pointerEvents="none" />
                          <rect data-ui="1" x={b.x + b.w - 4} y={b.y + b.h - 4} width={8} height={8} fill="#2563eb" stroke="#fff" strokeWidth={1} style={{ cursor: 'nwse-resize' }} onPointerDown={(e) => startResize(e, b)} data-testid={`fp-resize-${b.id}`} />
                        </>
                      )}
                    </g>
                  );
                })}
                {(layout.lines || []).map((ln) => {
                  const isS = editMode && sel && sel.kind === 'line' && sel.id === ln.id;
                  return (
                    <g key={ln.id} onPointerDown={editMode ? (e) => startLineMove(e, ln) : undefined} style={{ cursor: editMode && tool === 'select' ? 'move' : 'default' }} data-testid={`fp-line-${ln.id}`}>
                      <line x1={ln.x1} y1={ln.y1} x2={ln.x2} y2={ln.y2} stroke={ln.color} strokeWidth={ln.width} strokeLinecap="round" strokeDasharray={ln.dash ? `${ln.width * 3} ${ln.width * 2}` : undefined} />
                      {(ln.arrow === 'end' || ln.arrow === 'both') && <polygon points={arrowHead(ln.x1, ln.y1, ln.x2, ln.y2, ln.width)} fill={ln.color} />}
                      {ln.arrow === 'both' && <polygon points={arrowHead(ln.x2, ln.y2, ln.x1, ln.y1, ln.width)} fill={ln.color} />}
                      {editMode && <line data-ui="1" x1={ln.x1} y1={ln.y1} x2={ln.x2} y2={ln.y2} stroke="transparent" strokeWidth={Math.max(12, ln.width + 10)} />}
                      {isS && (
                        <>
                          <circle data-ui="1" cx={ln.x1} cy={ln.y1} r={4.5} fill="#fff" stroke="#2563eb" strokeWidth={1.5} style={{ cursor: 'grab' }} onPointerDown={(e) => startLineEnd(e, ln, 1)} />
                          <circle data-ui="1" cx={ln.x2} cy={ln.y2} r={4.5} fill="#fff" stroke="#2563eb" strokeWidth={1.5} style={{ cursor: 'grab' }} onPointerDown={(e) => startLineEnd(e, ln, 2)} />
                        </>
                      )}
                    </g>
                  );
                })}
              </svg>
            )}
          </div>
          <div className="absolute right-3 bottom-3 z-10 flex flex-col items-center gap-1 rounded-xl bg-white/95 shadow-lg border border-black/10 p-1" data-testid="floorplan-zoom-overlay">
            <button type="button" onClick={() => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-black/5 text-black" title="Zoom in" data-testid="fp-zoom-in"><ZoomIn className="w-4 h-4" /></button>
            <span className="text-[10px] font-bold text-black/60">{Math.round(zoom * 100)}%</span>
            <button type="button" onClick={() => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-black/5 text-black" title="Zoom out" data-testid="fp-zoom-out"><ZoomOut className="w-4 h-4" /></button>
            <button type="button" onClick={() => setZoom(1)} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-black/5 text-black" title="Pas layar"><Maximize2 className="w-4 h-4" /></button>
          </div>
          </div>

          <div className="mt-4 px-1 pb-1" data-testid="floorplan-placed-list">
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <div>
                <div className="font-display text-lg text-white">Tenant yang sudah di-placing</div>
                <div className="text-xs text-white/50">{placedRows.length} tenant | {totalBlocks} blok | total {fmtNum(totalArea)} m&sup2; | <span style={{ color: PAY.lunas.dot }}>Lunas {payCount.lunas || 0}</span> | <span style={{ color: PAY.dp.dot }}>DP {payCount.dp || 0}</span> | <span style={{ color: PAY.belum.dot }}>Belum bayar {payCount.belum || 0}</span> | <span style={{ color: PAY.barter.dot }}>Barter {payCount.barter || 0}</span> | nilai total <b className="text-white">{fmtRp(grandTotal)}</b>{ql ? ` | cocok ${shownRows.length}` : ''}</div>
              </div>
              <div className="flex gap-2"><button type="button" onClick={exportCsv} disabled={!placedRows.length} className="px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-white/80 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="floorplan-export-csv"><Download className="w-3.5 h-3.5" /> CSV</button>
              <button type="button" onClick={exportXlsx} disabled={!BLOCKS_LIVE.length} className="px-3 py-1.5 rounded-lg text-white text-xs font-black inline-flex items-center gap-1.5 disabled:opacity-40" style={{ background: '#1D7044' }} data-testid="floorplan-export-xlsx"><Download className="w-3.5 h-3.5" /> Excel / Sheets</button></div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3" data-testid="floorplan-money">
              {PAY_ORDER.map((k) => (
                <div key={k} className="rounded-xl border border-white/10 bg-black/20 p-3" style={{ borderLeft: `4px solid ${PAY[k].dot}` }}>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{{ lunas: 'Total lunas', dp: 'Total DP', belum: 'Total belum bayar', barter: 'Total barter (nilai)' }[k]}</div>
                  <div className="text-lg font-black text-white">{fmtRp(payTotal[k] || 0)}</div>
                  <div className="text-[11px] text-white/45">{payCount[k] || 0} tenant{k === 'barter' && revCount ? ` | ${revCount} sharing revenue` : ''}</div>
                </div>
              ))}
            </div>
            <div className="rounded-xl border border-white/10 bg-black/20 p-3 mb-3" data-testid="floorplan-unsold">
              <div className="flex items-end justify-between gap-3 flex-wrap mb-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">Booth belum laku</div>
                  <div className="text-lg font-black text-white">{unsold.count} blok <span className="text-white/40 font-bold text-sm">dari {unsold.total}</span> <span className="text-sm font-bold text-white/60">| {fmtNum(unsold.area)} m&sup2;</span></div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">Potensi pendapatan (harga standar)</div>
                  <div className="text-lg font-black" style={{ color: '#F5C518' }}>{fmtRp(unsold.value)}</div>
                </div>
              </div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden mb-3"><div className="h-full rounded-full" style={{ width: `${unsold.total ? Math.round(((unsold.total - unsold.count) / unsold.total) * 100) : 0}%`, background: '#10B981' }} /></div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                {unsold.groups.map((g) => (
                  <div key={g.k} className="rounded-lg border border-white/10 bg-white/5 p-2.5" style={{ borderLeft: `4px solid ${g.color}` }} data-testid={`fp-unsold-${g.k}`}>
                    <div className="text-[11px] font-bold text-white/70">{g.label}</div>
                    <div className="text-base font-black text-white">{g.empty.length} <span className="text-xs font-bold text-white/40">/ {g.total} blok kosong</span></div>
                    <div className="text-[11px] text-white/55">{fmtRp(g.value)} | {fmtNum(g.area)} m&sup2;</div>
                    {g.empty.length > 0 && g.empty.length <= 12 && <div className="text-[10px] text-white/40 mt-1 leading-snug">{g.empty.map((b) => blockTitle(b)).join(', ')}</div>}
                  </div>
                ))}
              </div>
            </div>
            {shownRows.length === 0 ? (
              <div className="text-sm text-white/40 py-4 text-center rounded-xl border border-white/5">{placedRows.length ? 'Tidak ada yang cocok dengan pencarian.' : 'Belum ada blok yang terisi.'}</div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-white/10 max-h-[60vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0" style={{ background: 'var(--tv-surface, #160F0F)' }}>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-white/40">
                      <th className="px-3 py-2 font-bold">Tenant</th><th className="px-3 py-2 font-bold">Blok</th><th className="px-3 py-2 font-bold">Ukuran Booth</th><th className="px-3 py-2 font-bold">Harga</th><th className="px-3 py-2 font-bold">Sharing</th><th className="px-3 py-2 font-bold">Sumber</th><th className="px-3 py-2 font-bold">Pembayaran</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shownRows.map((r) => (
                      <tr key={r.key} onClick={() => { setPickTenant(null); setSelBlock(r.b.id); }} className={`border-t border-white/5 cursor-pointer hover:bg-white/5 align-top ${r.blocks.some((b) => b.id === selBlock) ? 'bg-white/10' : ''}`} data-testid={`fp-row-${r.b.id}`}>
                        <td className="px-3 py-2 text-white font-semibold">{r.a.name}{r.a.missing && <span className="ml-2 text-[10px] text-red-300 font-bold">tidak approved</span>}{isUnfix(r.a) && <span className="ml-2 text-[10px] font-bold" style={{ color: '#FB923C' }}>belum PKS</span>}</td>
                        <td className="px-3 py-2 text-white">
                          <div className="flex flex-wrap gap-1">
                            {r.blocks.map((b) => (
                              <span key={b.id} className="inline-flex items-center gap-1 text-[11px] font-bold px-1.5 py-0.5 rounded border whitespace-nowrap" style={{ background: FP_ZONES[b.zone].fill, borderColor: FP_ZONES[b.zone].stroke, color: '#111' }}>{blockTitle(b)}</span>
                            ))}
                          </div>
                          <div className="text-[11px] text-white/45 mt-1">{r.zones}</div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="text-white font-bold">{fmtNum(r.area)} m&sup2;</div>
                          <div className="text-[11px] text-white/50">{r.sizeLabel}{r.blocks.length > 1 ? ` | ${r.blocks.length} blok digabung` : ''}</div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap"><PriceCell r={r} onSave={setPrice} /></td>
                        <td className="px-3 py-2 text-white/60">{r.a.partner || '-'}</td>
                        <td className="px-3 py-2 text-white/50 whitespace-nowrap">{SRC_LABEL[r.a.src] || r.a.src}{r.a.auto ? ' (otomatis)' : ''}</td>
                        <td className="px-3 py-2 whitespace-nowrap"><PayPills pk={r.pk} />{payments[r.pk] === 'barter' && <BarterCell pk={r.pk} value={barter[r.pk]} onSave={saveBarter} />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="tv-card p-4 space-y-3 xl:sticky xl:top-4" data-testid="floorplan-panel">
          {selB ? (
            <>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{FP_ZONES[selB.zone].name} | Booth {sizeTxt(selB.zone)}</div>
                  <div className="font-display text-xl text-white">Blok {blockTitle(selB)}</div>
                </div>
                <button type="button" onClick={() => setSelBlock(null)} className="p-1 rounded hover:bg-white/10 text-white"><X className="w-4 h-4" /></button>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                {selA ? (
                  <>
                    <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-1">Terisi oleh</div>
                    <div className="text-white font-bold">{selA.name}</div>
                    {selA.partner && <div className="text-xs text-white/60">Sharing dengan {selA.partner}</div>}
                    <div className="mt-2 flex items-center gap-2"><span className="text-[10px] uppercase tracking-widest text-white/40 font-bold">Pembayaran</span><PayPills pk={payKey(selA)} /></div>
                    {payments[payKey(selA)] === 'barter' && <BarterCell pk={payKey(selA)} value={barter[payKey(selA)]} onSave={saveBarter} />}
                    <div className="text-[11px] text-white/50 mt-1">
                      {SRC_LABEL[selA.src] || selA.src}{selA.auto ? ' | otomatis dari pilihan booth di form' : ''}
                      {selA.missing && <span className="text-red-300"> | data tenant sudah tidak approved / terhapus</span>}
                    </div>
                    <button type="button" onClick={() => clearBlock(selB.id)} className="mt-2 text-[11px] font-bold px-2.5 py-1.5 rounded-lg border border-white/10 bg-white/5 hover:bg-red-500/20 text-white/80 inline-flex items-center gap-1" data-testid="floorplan-clear"><Trash2 className="w-3 h-3" /> Kosongkan blok</button>
                  </>
                ) : <div className="text-sm text-white/50">Blok ini masih kosong.</div>}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{selA ? 'Ganti dengan tenant' : 'Pilih tenant'}</div>
            </>
          ) : (
            <>
              <div className="font-display text-lg text-white">Tenant fix belum dapat blok</div>
              <p className="text-xs text-white/50 -mt-2">Klik nama tenant, lalu klik blok di peta. Atau klik blok dulu untuk memilih tenant.</p>
            </>
          )}

          <div className="flex flex-wrap gap-1">
            {SOURCES.map(([k, l]) => (
              <button key={k} type="button" onClick={() => setSrc(k)} className={`text-[11px] font-bold px-2.5 py-1 rounded-lg border ${src === k ? 'bg-[#E63329] text-white border-transparent' : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'}`}>{l}</button>
            ))}
          </div>
          <label className="flex items-center justify-between gap-2 text-[11px] text-white/60 cursor-pointer" data-testid="floorplan-show-unfix">
            <span className="inline-flex items-center gap-1.5"><input type="checkbox" checked={showUnfix} onChange={(e) => setShowUnfix(e.target.checked)} className="accent-[#F97316]" /> Tampilkan yang belum PKS ({unfixCount})</span>
            {onb.loading && <Loader2 className="w-3 h-3 animate-spin" />}
          </label>
          <div className="relative">
            <Search className="w-4 h-4 text-white/40 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari tenant..." className="w-full bg-black/30 border border-white/10 rounded-lg pl-8 pr-3 py-2 text-sm text-white focus:outline-none focus:border-white/30" data-testid="floorplan-search" />
          </div>

          <div className="max-h-[46vh] overflow-y-auto -mx-1 px-1 space-y-1" data-testid="floorplan-tenant-list">
            {(selB ? listTenants : listTenants.filter((t) => !blocksOfTenant[t.key])).map((t) => {
              const at = blocksOfTenant[t.key];
              const active = pickTenant && pickTenant.key === t.key;
              return (
                <div key={t.key} role="button" tabIndex={0}
                  onClick={() => { if (selB) assignTo(selB.id, t); else setPickTenant(active ? null : t); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { if (selB) assignTo(selB.id, t); else setPickTenant(active ? null : t); } }}
                  className={`group w-full text-left px-3 py-2 rounded-lg border transition-colors cursor-pointer ${active ? 'border-[#E63329] bg-[#E63329]/15' : 'border-white/5 bg-white/[0.03] hover:bg-white/10'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-white font-semibold truncate">{t.name}</span>
                    <span className="flex items-center gap-1.5 flex-shrink-0">
                      {!t.pks && <button type="button" disabled={pksBusy === t.key} onClick={(e) => { e.stopPropagation(); markPks(t); }} title="Tandai sudah tanda tangan PKS" data-testid={`fp-pks-${t.key}`}
                        className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded border" style={{ color: '#FB923C', borderColor: '#F97316' }}>{pksBusy === t.key ? '...' : 'Tandai PKS'}</button>}
                      <span className="text-[9px] uppercase tracking-wider font-black text-white/40">{SRC_LABEL[t.src]}</span>
                      {t.src !== 'lineup' && t.src !== 'manual' && <button type="button" title="Hapus dari daftar fix (batalkan approval)" data-testid={`fp-del-${t.key}`}
                        onClick={(e) => { e.stopPropagation(); setDelTarget(t); setDelPw(''); setDelErr(''); }}
                        className="p-1 rounded-md text-white/40 hover:text-red-300 hover:bg-red-500/20"><Trash2 className="w-3.5 h-3.5" /></button>}
                    </span>
                  </div>
                  <div className="text-[11px] text-white/45 truncate">
                    {t.partner ? `Sharing: ${t.partner} | ` : ''}{at ? `Sudah di ${at.map((id) => blockTitle(BLOCKS_LIVE.find((b) => b.id === id))).join(', ')}` : 'Belum dapat blok'}
                  </div>
                </div>
              );
            })}
            {(selB ? listTenants : listTenants.filter((t) => !blocksOfTenant[t.key])).length === 0 && (
              <div className="text-xs text-white/40 py-6 text-center">{tenants.length === 0 ? 'Belum ada tenant approved / line up.' : selB ? (showUnfix ? 'Tidak ada tenant yang cocok.' : 'Tidak ada tenant PKS yang cocok. Centang "Tampilkan yang belum PKS".') : (showUnfix ? 'Semua tenant sudah dapat blok' : 'Semua tenant PKS sudah dapat blok')}</div>
            )}
          </div>

          {selB && (
            <div className="pt-2 border-t border-white/10">
              <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-1.5 inline-flex items-center gap-1"><Pencil className="w-3 h-3" /> Isi nama manual (di luar tracker)</div>
              <div className="flex gap-2">
                <input value={manual} onChange={(e) => setManual(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') assignManual(selB.id, manual); }} placeholder="Nama tenant / sponsor" className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none" data-testid="floorplan-manual-input" />
                <button type="button" onClick={() => assignManual(selB.id, manual)} className="px-3 rounded-lg text-xs font-bold text-white" style={{ background: 'var(--tv-accent)' }}>Isi</button>
              </div>
            </div>
          )}
        </div>
      </div>
      {delTarget && (
        <div className="fixed inset-0 z-[80] bg-black/70 flex items-start justify-center overflow-y-auto [&>*]:my-auto p-4" onClick={() => !delBusy && setDelTarget(null)} data-testid="floorplan-del-modal">
          <div className="tv-card p-5 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-white font-display text-lg"><Lock className="w-5 h-5 text-red-300" /> Hapus dari daftar fix?</div>
            <p className="text-sm text-white/70">
              <b className="text-white">{delTarget.name}</b> ({SRC_LABEL[delTarget.src]}) akan <b className="text-red-300">dibatalkan approval-nya</b> (status kembali ke <b>Pending</b> di tracker) dan dilepas dari semua blok di floorplan.
              {blocksOfTenant[delTarget.key] ? ` Blok yang dilepas: ${blocksOfTenant[delTarget.key].map((id) => blockTitle(BLOCKS_LIVE.find((b) => b.id === id))).join(', ')}.` : ''}
            </p>
            <input type="password" autoFocus value={delPw} onChange={(e) => setDelPw(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') doUnapprove(); }}
              placeholder="Password konfirmasi" className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-white/40" data-testid="floorplan-del-password" />
            {delErr && <div className="text-xs text-red-300">{delErr}</div>}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setDelTarget(null)} disabled={delBusy} className="px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-white/80 text-sm font-bold">Batal</button>
              <button type="button" onClick={doUnapprove} disabled={!delPw || delBusy} className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-black inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="floorplan-del-confirm">{delBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Hapus & batalkan approval</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
